import { useEffect, useState } from "react";
import { Dropdown } from "react-bootstrap";
import "react-calendar/dist/Calendar.css";
import DatePicker from "react-date-picker";
import "react-date-picker/dist/DatePicker.css";
import { DATEPICKER_PLACEHOLDER } from "../../../Constants/stringConstants";
import IconResource from "../../../Wrapper/IconResource/IconResource";
import "./DateRangePicker.scss";

import { safeDate } from "../../../Utils/dateUtils";

function DateRangePicker({
  startDate,
  showDateShortcuts,
  setStartDate,
  endDate,
  setEndDate,
  datePickerStartRef,
  datePickerEndRef,
  onChange,
  allowFutureDates = false,
}) {
  const [startValue, setStartValue] = useState(safeDate(startDate));
  const [endValue, setEndValue] = useState(safeDate(endDate));

  useEffect(() => {
    setStartValue(safeDate(startDate));
    setEndValue(safeDate(endDate));
  }, [startDate, endDate]);

  // Two callsite contracts are supported:
  //   1. Pass setStartDate + setEndDate (granular control of each pill)
  //   2. Pass a single onChange(start, end) and skip the setters entirely
  // TrackPatientUI uses #2. Use optional-call (`setX?.()`) on every call to
  // setStartDate/setEndDate so missing setters don't crash the component —
  // this regressed previously as: clicking a shortcut on a #2-style callsite
  // blew up with "setStartDate is not a function".
  //
  // We deliberately do NOT add `onChange(...)` calls inside the direct-pick
  // handlers below: most callers pass BOTH setters AND onChange (their
  // setters and onChange both call `setDates(...)` + a side-effect like
  // `handleDateSideEffect`). Firing both would double-call the side-effect
  // and fire wasted API requests. Direct-pick → setters is the existing
  // contract; shortcut click → setters + onChange is the existing contract.
  const handleStartDateChange = (date) => {
    setStartDate?.(date);
    setStartValue(date);

    let currentEndDate = endValue;
    // If the selected start date is after the end date, clear end date
    if (endValue && date > endValue) {
      setEndDate?.(null);
      setEndValue(null);
      currentEndDate = null;
    }

    if (onChange) {
      onChange(date, currentEndDate);
    }

    setTimeout(() => datePickerEndRef?.current?.focus(), 0);
  };

  const handleEndDateChange = (date) => {
    let currentStartDate = startValue;
    let currentEndDate = date;

    if (startValue > date) {
      setStartDate?.(date);
      setStartValue(date);
      setEndDate?.(date);
      setEndValue(date);
      currentStartDate = date;
      currentEndDate = date;
    } else {
      setEndDate?.(date);
      setEndValue(date);
    }

    if (onChange) {
      onChange(currentStartDate, currentEndDate);
    }
  };

  const ShortcutType = {
    Today: {
      key: "TODAY",
      displayValue: "Today",
    },
    Yesterday: {
      key: "YESTERDAY",
      displayValue: "Yesterday",
    },
    "This Week": {
      key: "THIS_WEEK",
      displayValue: "This Week",
    },
    "Last Week": {
      key: "LAST_WEEK",
      displayValue: "Last Week",
    },
    "This Month": {
      key: "THIS_MONTH",
      displayValue: "This Month",
    },
    "Last Month": {
      key: "LAST_MONTH",
      displayValue: "Last Month",
    },
    "This Year": {
      key: "THIS_YEAR",
      displayValue: "This Year",
    },
    "Last Year": {
      key: "LAST_YEAR",
      displayValue: "Last Year",
    },
    "Current FY": {
      key: "CURRENT_FY",
      displayValue: "Current FY",
    },
    "Last FY": {
      key: "LAST_FY",
      displayValue: "Last FY",
    },
    Clear: {
      key: "CLEAR",
      displayValue: "Clear",
    },
  };

  const handleShortcutClick = (shortcut, e) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let start,
      end = null;
    e.preventDefault();

    // Indian financial year runs 1 Apr - 31 Mar, matching the backend's
    // DateUtils::getFinancialYearFromDate (month >= 4 starts a new FY).
    const fyStartYear = today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;

    switch (shortcut) {
      case ShortcutType["Today"].displayValue:
        start = today;
        end = today;
        break;
      case ShortcutType["Yesterday"].displayValue:
        start = new Date(today);
        start.setDate(today.getDate() - 1);
        end = new Date(today);
        end.setDate(today.getDate() - 1);
        break;
      case ShortcutType["This Week"].displayValue:
        start = new Date(today);
        const dayOfWeek = today.getDay(); // 0 (Sun) to 6 (Sat)
        start.setDate(today.getDate() - dayOfWeek); // Start of week (Sunday)
        end = today;
        break;
      case ShortcutType["Last Week"].displayValue:
        start = new Date(today);
        const lastWeekDay = today.getDay();
        start.setDate(today.getDate() - lastWeekDay - 7);
        end = new Date(start);
        end.setDate(start.getDate() + 6);
        break;
      case ShortcutType["This Month"].displayValue:
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        end = today;
        break;
      case ShortcutType["Last Month"].displayValue:
        start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        end = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      case ShortcutType["This Year"].displayValue:
        start = new Date(today.getFullYear(), 0, 1);
        end = today;
        break;
      case ShortcutType["Last Year"].displayValue:
        start = new Date(today.getFullYear() - 1, 0, 1);
        end = new Date(today.getFullYear() - 1, 11, 31);
        break;
      // Ends on today, not 31 Mar: the FY is still running, and both pickers
      // cap at maxDate={new Date()} unless allowFutureDates, so a year-end
      // date would be rejected by the input anyway.
      case ShortcutType["Current FY"].displayValue:
        start = new Date(fyStartYear, 3, 1);
        end = today;
        break;
      case ShortcutType["Last FY"].displayValue:
        start = new Date(fyStartYear - 1, 3, 1);
        end = new Date(fyStartYear, 2, 31);
        break;
      case ShortcutType["Clear"].displayValue:
        start = null;
        end = null;
        break;
      default:
        return;
    }

    setStartValue(start);
    setEndValue(end);
    // Optional-call: callers that drive state purely via `onChange` (e.g.
    // TrackPatientUI) don't pass these setters. Without `?.()` the component
    // would throw "setStartDate is not a function" on every shortcut click.
    setStartDate?.(start);
    setEndDate?.(end);
    if (onChange) {
      onChange(start, end);
    }
  };

  return (
    // IMPORTANT: do NOT name this `.date-picker` — that class is owned by the
    // single-date <DatePickerWrapper> component (see _widgets/Datepicker) and
    // its global rules in Global.scss expect a label-on-top + input-below
    // block layout. A flex container called `.date-picker` would silently leak
    // into every <DatePickerWrapper> in the app and put labels next to inputs.
    <div className="date-range-picker">
      <div className="date-range-picker__field">
        <span className="date-range-picker__clear">
          <IconResource
            color="var(--primary)"
            size={18}
            type={startValue ? "cross" : ""}
            onClick={() => handleStartDateChange(null)}
          />
        </span>
        <DatePicker
          inputRef={datePickerStartRef}
          value={startValue}
          onChange={handleStartDateChange}
          format="dd/MM/yyyy"
          calendarIcon={<IconResource type="calendar" color="var(--primary)" />}
          clearIcon={null}
          maxDate={allowFutureDates ? null : new Date()}
          placeholder={DATEPICKER_PLACEHOLDER}
        />
      </div>

      <span className="date-range-picker__sep" aria-hidden="true">
        <IconResource type="rightArrow" size={16} color="#94a3b8" />
      </span>

      <div className="date-range-picker__field">
        <span className="date-range-picker__clear">
          <IconResource
            color="var(--primary)"
            size={18}
            type={endValue ? "cross" : ""}
            onClick={() => handleEndDateChange(null)}
          />
        </span>
        <DatePicker
          inputRef={datePickerEndRef}
          value={endValue}
          onChange={handleEndDateChange}
          format="dd/MM/yyyy"
          clearIcon={null}
          calendarIcon={<IconResource type="calendar" color="var(--primary)" />}
          minDate={startValue}
          maxDate={allowFutureDates ? null : new Date()}
          placeholder={DATEPICKER_PLACEHOLDER}
        />
      </div>

      {showDateShortcuts && (
        <Dropdown className="date-range-picker__shortcuts">
          <Dropdown.Toggle id="dropdown-basic" className="p-1">
            <IconResource type="dropdown" size={18} color="var(--primary)" />
          </Dropdown.Toggle>
          <Dropdown.Menu>
            {Object.keys(ShortcutType).map((key) => (
              <Dropdown.Item
                key={key}
                onClick={(e) => handleShortcutClick(ShortcutType[key].displayValue, e)}
              >
                {ShortcutType[key].displayValue}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      )}
    </div>
  );
}

export default DateRangePicker;
